import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../@types/redux';
import { dataSetVarieties } from '../../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../../services/appFunctionsService';
import { ICommodity } from '../../../../@types/model/masterData/commodity/commodity';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../../store/general/Functions';
import VarietyHttpService from '../../../../services/http/masterData/varietyHttpService';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../../@types/other';
import { setVarietyMasterDataIndexedDB, syncMasterData } from '../../../../services/masterDataSyncService';
import VarietyForm from './form/VarietyForm';
import { useLocation } from 'react-router-dom';
import { IVariety } from '../../../../@types/model/masterData/variety/variety';
import { IVarietyFormValues } from '../../../../@types/model/masterData/variety/varietyFormValues';
import VarietyModelHelper from '../../../../@types/model/masterData/variety/varietyModelHelper';
import UserHelper from '../../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../../@types/enum/rightGrantsEnum';

interface IVarietyScreenProps {
    selectedCommodity : ICommodity | null;
}

const VarietyScreen = (props : IVarietyScreenProps) : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const varieties = useAppSelector(x => x.masterData.varieties);
    const commodities = useAppSelector(x => x.masterData.commodities);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedVariety, setSelectedVariety] = useState<IVariety | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingVariety, setDeletingVariety] = useState<IVariety | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await VarietyHttpService.getVarietyData();
                dispatch(dataSetVarieties(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading varieties.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IVarietyFormValues) => {
        setIsLoading(true);

        try {
            const upsert = VarietyModelHelper.createUpsert(values);
            const res = await VarietyHttpService.addOrUpdateVariety(upsert);
            const updatedList = upsertArrayElement(varieties, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetVarieties(updatedList));
            await setVarietyMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedVariety ? 'Variety updated successfully.' : 'Variety added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating variety data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeVariety = async () => {
        if (!deletingVariety) return;
        const updatedVariety = { ...deletingVariety, isActive: false };
        setIsLoading(true);

        try {
            const res = await VarietyHttpService.deleteVariety(updatedVariety.id);
            const updatedList = upsertArrayElement(varieties, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetVarieties(updatedList));
            await setVarietyMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Variety deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting variety.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedVariety(null);
    };

    const edit = (selectedRow : IVariety) => {
        setSelectedVariety(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IVariety) : void => {
        setDeletingVariety(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingVariety(null);
    };

    const addButtonTooltip = () : string => {
        let message = 'Add';

        if (!userHasRights.addRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Variety', RightGrantsEnum.Add);
        } else if (!props.selectedCommodity) {
            message = 'Commodity Not Selected';
        }

        return message;
    };

    const deleteButtonTooltip = (row : IVariety) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Variety', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        } else if (row.isImported) {
            message = 'Cannot delete imported entries.';
        }

        return message;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return VarietyModelHelper.createFormValues(selectedVariety, commodities, props.selectedCommodity);
        }
    }, [selectedVariety, isDialogOpen, commodities, props.selectedCommodity]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const rows = useMemo(() => {
        return varieties.filter(x => x.commodityId === props.selectedCommodity?.id);
    }, [props.selectedCommodity, varieties]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <>
            <Card className='fdc hfill'>
                <CustomTable<IVariety>
                        
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || !props.selectedCommodity || isLoading}
                    addTooltip={addButtonTooltip()}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Variety', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : IVariety) : boolean => !userHasRights.deleteRight || !row.isActive || row.isImported || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}
                
                    enableSorting
                    enableFiltering
                    enablePagination
                    fitWidthToPage
                    columns={[
                        { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Imported?', field: 'isImported', type: 'boolean', enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={rows}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={340}
                    isActive={row => row.isActive}
                />
            </Card>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedVariety ? 'Edit Variety' : 'Add Variety'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <VarietyForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isImported={selectedVariety?.isImported ?? false}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Variety'}
                open={!!deletingVariety}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeVariety}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </>
    );
};

export default VarietyScreen;
